import type { ReactNode } from 'react'
import cx from 'classnames'
import { Text } from '@/components/Text'
import type { PricingInfo } from '@/features/jobs/drafting/quote-display'
import {
  SubmissionSection,
  SubmissionSectionHeader,
} from '@/features/jobs/drafting/components/JobCreation/Submission/SubmissionSection'
import styles from './Pricing.module.css'

export interface PricingProps {
  pricing?: PricingInfo | null
  /**
   * If `true`, will render a section title and put
   * the items in a box. The box will stretch.
   */
  boxed?: boolean
  compact?: boolean
}

export function Pricing({ pricing, boxed, compact }: PricingProps) {
  if (!pricing) {
    return null
  }

  const items = (
    <>
      {pricing.discount && (
        <PricingItem highlighted label="Discount" value={pricing.discount} />
      )}
      <PricingItem label="Total" value={pricing.total} />
      <PricingItem label="Amount Due Now" value={pricing.amountDueNow} />
    </>
  )

  return (
    <SubmissionSection compact={compact}>
      {boxed && <SubmissionSectionHeader label="Summary" compact={compact} />}
      {boxed ? <Box>{items}</Box> : items}
    </SubmissionSection>
  )
}

interface PricingItemProps {
  label: string
  value: string
  highlighted?: boolean
}

function PricingItem({ label, value, highlighted }: PricingItemProps) {
  if (!value) {
    return null
  }

  return (
    <div className={cx(styles.pricingItem, highlighted && styles.highlighted)}>
      <div className={styles.itemLabel}>{label}</div>
      <div
        className={styles.itemValue}
        data-test={`pricing-item-${label}-value`}
      >
        <Text text={value} />
      </div>
    </div>
  )
}

interface BoxProps {
  children?: ReactNode
}

function Box({ children }: BoxProps) {
  return <div className={styles.box}>{children}</div>
}
